import { useMutation } from '@tanstack/react-query';
import { GoogleGenerativeAI } from '@google/generative-ai';

export interface QuizQuestion {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  category: string;
}

interface GenerateQuizParams {
  title: string;
  content: string;
  subject?: string | null;
  language?: string | null;
}

const apiKey = import.meta.env.VITE_GEMINI_API_KEY;

async function generateQuiz(params: GenerateQuizParams): Promise<QuizQuestion[]> {
  if (!apiKey) {
    throw new Error('Gemini API key not configured. Set VITE_GEMINI_API_KEY in .env');
  }

  const { title, content, subject, language } = params;

  const languageInstruction = language
    ? `Generate the quiz in ${language} language.`
    : 'Generate the quiz in English.';

  const genAI = new GoogleGenerativeAI(apiKey);
  const model = genAI.getGenerativeModel({
    model: 'gemini-2.5-flash-lite',
    generationConfig: {
      temperature: 0.7,
      responseMimeType: 'application/json',
    },
  });

  const prompt = `You are a quiz generator for educational content. Generate exactly 5 multiple-choice questions based on the provided lesson content. Each question should have exactly 5 answer options (A through E). ${languageInstruction}

Return a JSON object with this exact structure:
{
  "questions": [
    {
      "id": 1,
      "question": "Question text here",
      "options": ["Option A", "Option B", "Option C", "Option D", "Option E"],
      "correctAnswer": 0,
      "category": "short-category-name"
    }
  ]
}

Rules:
- "correctAnswer" is the zero-based index of the correct option (0-4)
- "category" should be a short kebab-case label describing the topic area (e.g. "vocabulary", "comprehension", "grammar", "math-operations", "concepts")
- Questions should test understanding of the lesson content
- Mix difficulty levels: 2 easy, 2 medium, 1 hard
- Options should be plausible but only one correct

Lesson title: ${title}
${subject ? `Subject: ${subject}` : ''}

Lesson content:
${content}`;

  const result = await model.generateContent(prompt);
  const raw = result.response.text();

  if (!raw) throw new Error('Empty response from Gemini');

  const parsed = JSON.parse(raw);
  return parsed.questions as QuizQuestion[];
}

export const useGenerateQuiz = () => {
  return useMutation({
    mutationFn: generateQuiz,
  });
};
